import { Injectable } from '@angular/core';

export interface ScheduledRangePayload {
  start_time_utc: string;
  end_time_utc: string;
  timezone: string;
}

@Injectable({
  providedIn: 'root',
})
export class TimezoneService {
  private readonly userTimezoneKey = 'erp.user.timezone';

  getBrowserTimezone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  }

  getUserTimezone(): string {
    const saved = globalThis.localStorage?.getItem(this.userTimezoneKey) || '';
    return this.validateCandidate(saved) || this.getBrowserTimezone();
  }

  setUserTimezone(timezone?: string | null): string {
    const resolved = this.validateCandidate(`${timezone || ''}`.trim()) || this.getBrowserTimezone();
    globalThis.localStorage?.setItem(this.userTimezoneKey, resolved);
    return resolved;
  }

  nowUtcIso(): string {
    return new Date().toISOString();
  }

  validateTimezone(timezone?: string | null): string {
    const candidate = `${timezone || ''}`.trim() || this.getUserTimezone();
    return this.validateCandidate(candidate) || 'UTC';
  }

  private validateCandidate(candidate: string): string | null {
    if (!candidate) return null;
    try {
      Intl.DateTimeFormat('en-US', { timeZone: candidate });
      return candidate;
    } catch {
      return null;
    }
  }

  localInputToUtcIso(localInput: string, timezone?: string | null): string {
    const value = `${localInput || ''}`.trim();
    if (!value) {
      return '';
    }

    const resolvedTimezone = this.validateTimezone(timezone);
    const localParts = this.parseLocalInput(value);
    if (!localParts) {
      return '';
    }

    const utcDate = this.zonedLocalToUtc(localParts, resolvedTimezone);
    return utcDate.toISOString();
  }

  utcIsoToLocalInput(utcIso: string, timezone?: string | null): string {
    const date = this.parseUtcDate(utcIso);
    if (!date) {
      return '';
    }

    const resolvedTimezone = this.validateTimezone(timezone);
    return this.toLocalInputValue(date, resolvedTimezone);
  }

  formatUtcForDisplay(
    utcIso: string,
    timezone?: string | null,
    locale = 'en-US',
    includeSeconds = false
  ): string {
    const date = this.parseUtcDate(utcIso);
    if (!date) {
      return '';
    }

    const resolvedTimezone = this.validateTimezone(timezone);
    return new Intl.DateTimeFormat(locale, {
      timeZone: resolvedTimezone,
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: includeSeconds ? '2-digit' : undefined,
      timeZoneName: 'short',
      hour12: false,
    }).format(date);
  }

  buildScheduledRangePayload(
    startLocalInput: string,
    endLocalInput: string,
    timezone?: string | null
  ): ScheduledRangePayload | null {
    const resolvedTimezone = this.validateTimezone(timezone);
    const startUtc = this.localInputToUtcIso(startLocalInput, resolvedTimezone);
    const endUtc = this.localInputToUtcIso(endLocalInput, resolvedTimezone);

    if (!startUtc || !endUtc) {
      return null;
    }

    return {
      start_time_utc: startUtc,
      end_time_utc: endUtc,
      timezone: resolvedTimezone,
    };
  }

  private parseUtcDate(value: string): Date | null {
    const trimmed = `${value || ''}`.trim();
    if (!trimmed) {
      return null;
    }

    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }

    return parsed;
  }

  private parseLocalInput(value: string): {
    year: number;
    month: number;
    day: number;
    hour: number;
    minute: number;
    second: number;
  } | null {
    const normalized = value.trim().replace(' ', 'T');
    const [datePart, timePart = '00:00'] = normalized.split('T');
    if (!datePart) {
      return null;
    }

    const dateChunks = datePart.split('-').map((chunk) => Number(chunk));
    if (dateChunks.length !== 3 || dateChunks.some((chunk) => !Number.isFinite(chunk))) {
      return null;
    }

    const timeChunks = timePart.split(':').map((chunk) => Number(chunk));
    const [hour = 0, minute = 0, second = 0] = timeChunks;
    if ([hour, minute, second].some((chunk) => !Number.isFinite(chunk))) {
      return null;
    }

    return {
      year: dateChunks[0],
      month: dateChunks[1],
      day: dateChunks[2],
      hour,
      minute,
      second,
    };
  }

  private zonedLocalToUtc(
    local: { year: number; month: number; day: number; hour: number; minute: number; second: number },
    timezone: string
  ): Date {
    const utcGuessMs = Date.UTC(local.year, local.month - 1, local.day, local.hour, local.minute, local.second);

    const offsetMsFirst = this.computeZoneOffsetMs(utcGuessMs, timezone);
    const candidateMs = utcGuessMs - offsetMsFirst;

    const offsetMsSecond = this.computeZoneOffsetMs(candidateMs, timezone);
    const finalMs = utcGuessMs - offsetMsSecond;

    return new Date(finalMs);
  }

  private computeZoneOffsetMs(utcEpochMs: number, timezone: string): number {
    const parts = this.getDateTimePartsInZone(new Date(utcEpochMs), timezone);
    const asUtcMs = Date.UTC(
      parts.year,
      parts.month - 1,
      parts.day,
      parts.hour,
      parts.minute,
      parts.second
    );

    return asUtcMs - utcEpochMs;
  }

  private toLocalInputValue(date: Date, timezone: string): string {
    const parts = this.getDateTimePartsInZone(date, timezone);
    return [
      `${parts.year}`.padStart(4, '0'),
      `${parts.month}`.padStart(2, '0'),
      `${parts.day}`.padStart(2, '0'),
    ].join('-') + `T${`${parts.hour}`.padStart(2, '0')}:${`${parts.minute}`.padStart(2, '0')}`;
  }

  private getDateTimePartsInZone(
    date: Date,
    timezone: string
  ): { year: number; month: number; day: number; hour: number; minute: number; second: number } {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });

    const parts = formatter.formatToParts(date);
    const getPart = (type: string): number =>
      Number(parts.find((part) => part.type === type)?.value || '0');

    return {
      year: getPart('year'),
      month: getPart('month'),
      day: getPart('day'),
      hour: getPart('hour'),
      minute: getPart('minute'),
      second: getPart('second'),
    };
  }
}
